import React from 'react';
import classNames from 'classnames';

import Data from 'data';
import Store from 'store';
import Locale from 'data/locale';
import { D3TooltipCube } from 'tooltips';
import { ItemIcon, SkillHeader, SkillActive, SkillInfo, SkillParams, SkillBoxes } from './skillInfo';
import { cubeGetStat, cubeItemAllowed, cubeSlotValid, cubeTypes } from 'logic/cube';
import ItemSelect from 'components/equipment/ItemSelect';
import { getStats } from 'logic/stats';
import EditingContext from 'store/editingContext';
import scrollIntoView from 'scroll-into-view-if-needed';
import { useDragDrop, useItemMover } from 'components/dragDrop';

const _L = Locale("ui-skills.js");

export const CubeItemSelect = React.forwardRef(function CubeItemSelect({ type, id, onChange, className, valueClass, ...props }, ref) {
  const charClass = Store.useProfile(profile => profile.class);
  const seasonal = Store.useProfile(profile => profile.seasonal);
  const hideLegacy = Data.useOption("hideLegacy");

  const getOptions = React.useCallback(() => {
    const groups = {};
    for (const item of Data.items) {
      if (hideLegacy && item.legacy) continue;
      if (!cubeItemAllowed({class: charClass, seasonal}, type, item.id)) continue;
      if (!groups[item.type]) {
        groups[item.type] = {
          title: Data.itemTypes[item.type].name,
          options: [],
        };
      }
      groups[item.type].options.push(item);
    }
    const result = Object.values(groups);
    for (const group of result) {
      group.options.sort((a, b) => a.name.localeCompare(b.name));
      group.options = group.options.map(item => item.id);
    }
    return result;
  }, [charClass, seasonal, type, hideLegacy]);

  return <ItemSelect
    className={className}
    valueClass={valueClass}
    options={getOptions}
    placeholder={<span className={valueClass}>{_L`Choose ${cubeTypes[type].name()}`}</span>}
    value={id}
    onChange={onChange}
    searchLimit={10}
    allowEmpty={true}
    ref={ref}
    virtualScroll
    cube
    {...props}
  />;
});

export const CubeCard = React.memo(function CubeCard({ type }) {
  const dispatch = Store.useDispatch();

  const charClass = Store.useProfile(profile => profile.class);
  const seasonal = Store.useProfile(profile => profile.seasonal);
  const itemId = Store.useProfile(profile => profile.kanai[type]);
  const setItem = React.useCallback(id => dispatch.profile.setCubeItem(type, id), [dispatch, type]);

  const item = itemId && Data.itemById[itemId];
  const [affix, value] = (item && cubeGetStat(item.id)) || [];
  const info = affix && Data.itemaffixes[affix];

  const shown = Store.useSelector(state => {
    if (!info) return false;
    const stats = getStats(state);
    if (info.check) {
      if (typeof info.info === "function") {
        if (info.info([value], stats)) return true;
      } else if (info.info) {
        return true;
      }
      if (info.selectActive(state) != null || info.params) {
        if (typeof info.buffs === "function") {
          if (info.buffs([value], stats)) return true;
        } else if (info.buffs) {
          return true;
        }
      }
      return false;
    } else {
      return !!(info.info || info.selectActive(state) != null || info.params);
    }
  }, [info, value]);

  const headerRef = React.useRef();
  const selectRef = React.useRef();
  const onEdit = React.useCallback(params => {
    if (params.type === "cube" && type === params.slot) {
      scrollIntoView(headerRef.current, {scrollMode: "if-needed", block: "center", inline: "center"});
      selectRef.current.focus();
      return true;
    }
  }, [type]);
  EditingContext.useHandler(onEdit);

  const itemMover = useItemMover();

  const [dragClass, drag, drop] = useDragDrop("ITEM", {
    canDrag: () => item != null,
    begin: () => ({type: "ITEM", id: item.id}),
    end: (item, monitor) => itemMover({location: "cube", slot: type}, monitor.getDropResult()),
  }, {
    canDrop: item => item.type === "ITEM" && cubeItemAllowed({seasonal, class: charClass}, type, item.id),
    drop: () => ({location: "cube", slot: type}),
  });

  return (
    <div className={classNames("d3p-SkillCard d3p-cube", dragClass)} ref={drop}>
      <D3TooltipCube id={item && itemId} disabled={dragClass === "d3p-dragging"}>
        <SkillHeader icon={item && <ItemIcon id={item.id} ref={drag}/>}
                     name={<CubeItemSelect type={type} id={itemId} onChange={setItem} ref={selectRef} className="d3p-cube-item ui-Select-noframe" valueClass="d3p-cube-item-name"/>}
                     ref={headerRef}
        >
          {shown && <div className="d3p-passive-extra">
            {!!info.setActive && <SkillActive info={info}/>}
            {!!info.boxnames && <SkillBoxes info={info}/>}
            {!!info.params && <SkillParams info={info}/>}
            {!!info.info && <SkillInfo type="affix" id={affix}/>}
          </div>}
        </SkillHeader>
      </D3TooltipCube>
    </div>
  );
});

export default function CubeSection({ noHeader }) {
  const seasonal = Store.useProfile(profile => profile.seasonal);
  return (
    <React.Fragment>
      {!noHeader && <h3 className="d3p-skill-category">{_L`Kanai's Cube`}</h3>}
      {Object.keys(cubeTypes).filter(type => cubeSlotValid({seasonal}, type)).map(type => <CubeCard key={type} type={type}/>)}
    </React.Fragment>
  );
}
